import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Popover, Checkbox } from 'antd';

// helpers
import { cn } from '../../../helper/cn';

// hooks
import useToggle from '../../../hooks/useToggle';

// components
import RadioButton from '../../form/radio/RadioButton';

const Filters = ({
  isLoading,
  type,
  lists,
  setLists,
  onSelect,
  label,
  rootClassName,
  labelClassName,
  listTitleStyle,
  arrow,
  disabled,
  reset,
}) => {
  const [initialList, setInitialList] = useState(lists);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isOpen, setIsOpen] = useToggle(false);

  useEffect(() => {
    setInitialList(lists);
    const selected = lists.filter(item => item.isSelected).map(item => item.id);
    setSelectedIds(selected);
  }, [lists]);

  const singleSelected = lists.find(item => item.isSelected);

  const handleFilterChange = useCallback(
    item => {
      if (type === 'multiple') {
        const isSelected = selectedIds.includes(item.id);
        const updatedIds = isSelected
          ? selectedIds.filter(id => id !== item.id)
          : [...selectedIds, item.id];
        setSelectedIds(updatedIds);
        setLists(
          lists.map(list =>
            list.id === item.id ? { ...list, isSelected: !isSelected } : list
          )
        );
      } else {
        const isSame = selectedIds[0] === item.id;
        const newSelected = isSame ? [] : [item.id];
        setSelectedIds(newSelected);
        setLists(
          lists.map(list => ({
            ...list,
            isSelected: !isSame && list.id === item.id,
          }))
        );
      }
    },
    [selectedIds, lists, setLists, type]
  );

  const clearAll = useCallback(() => {
    setLists(initialList);
    setSelectedIds([]);
    setIsOpen(false);
    reset(true);
  }, [initialList, setLists, setIsOpen, reset]);

  const displayLabel = () => {
    if (singleSelected?.filter) return singleSelected.label;
    if (type === 'multiple') {
      if (selectedIds.length === 0 || selectedIds.length === lists.length) {
        return '(All)';
      }
      if (selectedIds.length === 1) {
        return `(${singleSelected?.label})`;
      }
      return selectedIds.length;
    }
    return singleSelected ? `(${singleSelected.label})` : label;
  };

  return (
    <Popover
      trigger={disabled ? [] : ['click']}
      open={isOpen}
      arrow={false}
      placement="bottom"
      onOpenChange={setIsOpen}
      content={
        <div className="min-w-56 px-3.5 py-2.5">
          {!singleSelected?.filter && (
            <div className="w-full h-8 flex items-center justify-between gap-5 py-2.5 text-sm font-medium">
              <span className="text-[var(--font-400)]">{label}</span>
              <button
                type="button"
                className={cn(
                  'text-[#F96056]',
                  selectedIds.length > 0
                    ? 'cursor-pointer opacity-100'
                    : 'cursor-not-allowed opacity-50'
                )}
                onClick={clearAll}
                disabled={selectedIds.length === 0}
              >
                Clear
              </button>
            </div>
          )}
          {lists.map(t =>
            t.type === 'title' ? (
              <div
                key={`title-${t.label}`}
                className={cn(
                  'text-sm font-medium text-[var(--font-400)] pb-1 pt-2.5',
                  listTitleStyle
                )}
              >
                {t.label}
              </div>
            ) : (
              <div
                key={t.id}
                className={cn(
                  'w-full h-9 flex items-center gap-2.5',
                  t.isDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer',
                  t.filter && 'pl-2.5'
                )}
                onClick={() => {
                  if (!disabled && onSelect && !t.isDisabled) {
                    onSelect(t);
                  }
                  if (!t.isDisabled) handleFilterChange(t);
                }}
              >
                {type === 'multiple' ? (
                  <Checkbox
                    checked={t.isSelected}
                    onChange={() => handleFilterChange(t)}
                    className="size-4"
                    disabled={t.isDisabled}
                  />
                ) : (
                  <RadioButton
                    selected={t.isSelected}
                    rootClassName="size-4"
                    onChange={() => handleFilterChange(t)}
                  />
                )}
                {t.icon && <img src={t.icon} alt={t.label} />}
                <p className="text-sm font-medium">{t.label}</p>
              </div>
            )
          )}
        </div>
      }
    >
      <div
        className={cn(
          'min-w-32 group/goal flex items-center gap-2.5 bg-[var(--BG-25)] px-5 py-1.5 rounded-lg',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          rootClassName
        )}
      >
        <div className="campaign-filters flex items-center gap-1">
          {!singleSelected?.filter && (
            <p className={cn(labelClassName)}>{label}</p>
          )}
          {singleSelected?.icon && (
            <img src={singleSelected.icon} alt={singleSelected.label} />
          )}
          <div
            className={cn(
              'ml-1',
              selectedIds.length > 1 && selectedIds.length < lists.length &&
                'rounded-full bg-[#6940F2] text-white text-[0.812rem] w-[1.3vw] h-[1.3vw] flex justify-center items-center'
            )}
          >
            {displayLabel()}
          </div>
        </div>
        {arrow && (
          <div
            className={cn(
              'transition-transform duration-200 ease-linear',
              isOpen ? '-rotate-180' : 'rotate-0',
              isLoading ? 'cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            <div className="border-[0.4vw] rounded-t-[0.2vw] border-transparent border-t-[#777] translate-y-[25%]" />
          </div>
        )}
      </div>
    </Popover>
  );
};

Filters.propTypes = {
  isLoading: PropTypes.bool,
  type: PropTypes.oneOf(['single', 'multiple']),
  lists: PropTypes.array.isRequired,
  setLists: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  label: PropTypes.string,
  rootClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  listTitleStyle: PropTypes.string,
  arrow: PropTypes.bool,
  disabled: PropTypes.bool,
};

Filters.defaultProps = {
  isLoading: false,
  type: 'multiple',
  onSelect: null,
  label: '',
  rootClassName: '',
  labelClassName: '',
  listTitleStyle: '',
  arrow: true,
  disabled: false,
};

export default Filters;
